import { UserCredentials } from '../types/userCredentials';
import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';

const getUser = async () => {
  const userSessionToken = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/user`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });

  if (!res.ok) {
    switch (res.status) {
      case 404:
        return null;
      default:
        return null;
    }
  }

  return res.json();
};

const login = async (token: string, stytch_token_type: string) => {
  const res = await fetch(`${BASE_URL}/signin/oauth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token, stytch_token_type })
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.detail || `Login failed with status ${res.status}`
    );
  }

  return res.json();
};

const logout = async () => {
  const userSessionToken = getUserSessionCookie();
  if (!userSessionToken) {
    throw new Error('User session token not found');
  }

  const res = await fetch(`${BASE_URL}/logout?token=${userSessionToken}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-app-name': 'DOKAI'
    }
  });

  if (!res.ok) {
    throw new Error('Logout failed');
  }

  return res.json();
};

const setCredentials = async (
  accountType: string,
  userCredentials: UserCredentials
) => {
  const res = await fetch(`${BASE_URL}/user/credentials/${accountType}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(userCredentials)
  });

  return res.json();
};

const getCredentials = async (accountType: string, userId: string) => {
  const res = await fetch(
    `${BASE_URL}/user/credentials/${accountType}?user_id=${userId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return res.json();
};

export type SendMagicLoginLinkResponse = {
  message: string;
  status?: 'waitlist' | 'success';
};

const sendMagicLoginLink = async (
  email: string
): Promise<SendMagicLoginLinkResponse> => {
  const res = await fetch(`${BASE_URL}/auth/email/send_login_link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!res.ok) {
    const errorData = await res.json();
    const errMsg = errorData.detail || 'Failed to send magic link';
    throw new Error(errMsg);
  }
  return res.json();
};

const sendEmailOTP = async (email: string): Promise<any> => {
  try {
    const res = await fetch(`${BASE_URL}/auth/email/send_otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!res.ok) {
      const errorData = await res.json();
      const errMsg = errorData.detail || 'Failed to send OTP';
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (error: any) {
    throw error;
  }
};

const verifyEmailOTP = async (email: string, otp: string): Promise<any> => {
  const payload: any = { email, otp };
  const res = await apiFetch(`${BASE_URL}/signin/email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-app-name': 'DOKAI'
    },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const errorData = await res.json();
    const errMsg = errorData.detail || 'OTP verification failed';
    throw new Error(errMsg);
  }
  return res.json();
};

export const resolveEmailToOrganizationAuthInfo = async (
  email: string
): Promise<any> => {
  const res = await fetch(`${BASE_URL}/sso/resolve-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  if (!res.ok) {
    let errMsg = 'Failed to resolve email for SSO';
    try {
      const data = await res.json();
      errMsg = data.detail || errMsg;
    } catch {
      errMsg = (await res.text()) || errMsg;
    }
    throw new Error(errMsg);
  }

  return res.json();
};

export {
  getUser,
  login,
  logout,
  setCredentials,
  getCredentials,
  sendMagicLoginLink,
  sendEmailOTP,
  verifyEmailOTP
};
